import React, { useState, useEffect, useCallback } from "react";
import {
  Card,
  Table,
  Button,
  Space,
  message,
  Spin,
  Select,
} from "antd";
import { FaArrowLeft, FaDownload } from "react-icons/fa";
import moment from "moment";
import {
  getCurrentFinancialYear,
  getFinancialYearOptions,
} from "../utils/financialYearUtils";
import useLatestRequest from "../utils/useLatestRequest";

const { Option } = Select;

const BillEntryReport = ({ onBack }) => {
  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [downloading, setDownloading] = useState(false);
  const [financialYear, setFinancialYear] = useState(getCurrentFinancialYear());

  const beginRequest = useLatestRequest();

  const fetchData = useCallback(
    async (isExcel = false) => {
      const isCurrent = isExcel ? () => true : beginRequest();
    if (isExcel) setDownloading(true);
    else {
      setLoading(true);
      setRows([]);
    }
    try {
      const params = new URLSearchParams();
      params.set("financialYear", financialYear);
      if (isExcel) params.set("excel", "true");
      const q = `?${params.toString()}`;
      const url = `${apiBaseUrl}/inventoryManagement/getBillEntryReport${q}`;

      const res = await fetch(url, {
        method: "GET",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
      });

      if (isExcel) {
        if (!res.ok) {
          message.error("Failed to download Excel");
          return;
        }
        const blob = await res.blob();
        const downloadUrl = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = downloadUrl;
        a.download = `Bill_Entry_${financialYear}_${moment().format("YYYY-MM-DD")}.xlsx`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(downloadUrl);
        return;
      }

      const j = await res.json();
      if (!isCurrent()) return;
      if (j.success) setRows(j.data || []);
      else {
        console.error("getBillEntryReport failed:", j.message);
        message.error("Failed to fetch bill entry report");
      }
    } catch (err) {
      console.error(err);
      message.error(isExcel ? "Error downloading Excel" : "Error fetching report");
    } finally {
      if (isCurrent()) {
        setLoading(false);
        setDownloading(false);
      }
    }
    },
    [apiBaseUrl, financialYear]
  );

  useEffect(() => {
    fetchData(false);
  }, [fetchData]);

  const handleDownloadExcel = () => {
    fetchData(true);
  };

  const formatAmount = (v) =>
    typeof v === "number" ? new Intl.NumberFormat("en-IN").format(v) : (v ?? "-");

  const columns = [
    {
      title: "Invoice No.",
      dataIndex: "invoiceNo",
      key: "invoiceNo",
      width: 140,
      align: "center",
    },
    {
      title: "Bill Date",
      dataIndex: "billDate",
      key: "billDate",
      width: 110,
      align: "center",
      render: (d) => (d ? moment(d).format("DD-MM-YYYY") : "-"),
    },
    {
      title: "Party Name",
      dataIndex: "partyName",
      key: "partyName",
      width: 220,
      align: "left",
    },
    {
      title: "Party GST No.",
      dataIndex: "partyGstNumber",
      key: "partyGstNumber",
      width: 150,
      align: "center",
      render: (v) => v || "-",
    },
    {
      title: "HO Amount",
      dataIndex: "hoAmount",
      key: "hoAmount",
      width: 110,
      align: "right",
      render: formatAmount,
    },
    {
      title: "PRD Amount",
      dataIndex: "prdAmount",
      key: "prdAmount",
      width: 110,
      align: "right",
      render: formatAmount,
    },
    {
      title: "Brand",
      dataIndex: "brand",
      key: "brand",
      width: 160,
      align: "left",
    },
    {
      title: "Salesman",
      dataIndex: "salesman",
      key: "salesman",
      width: 140,
      align: "left",
    },
    {
      title: "Cancel Remark",
      dataIndex: "cancelremark",
      key: "cancelremark",
      width: 200,
      align: "left",
      render: (v) => (v && String(v).trim() ? v : "-"),
    },
  ];

  const components = {
    header: {
      cell: (props) => (
        <th
          {...props}
          style={{
            ...props.style,
            backgroundColor: "#daeffe",
          }}
        />
      ),
    },
  };

  return (
    <div
      className="parent-padding-container"
      style={{ backgroundColor: "white", borderRadius: 12, padding: 20 }}
    >
      <Card
        title={
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Space>
              <Button icon={<FaArrowLeft />} onClick={onBack} type="text" />
              Bill Entry
            </Space>

            <Space>
              <Select
                value={financialYear}
                onChange={setFinancialYear}
                style={{ width: 120 }}
                placeholder="Select FY"
              >
                {getFinancialYearOptions().map((fy) => (
                  <Option key={fy} value={fy}>
                    {fy}
                  </Option>
                ))}
              </Select>
              <Button
                type="primary"
                icon={<FaDownload />}
                loading={downloading}
                onClick={handleDownloadExcel}
              >
                Download Excel
              </Button>
            </Space>
          </div>
        }
      >
        {loading ? (
          <div style={{ textAlign: "center", padding: 60 }}>
            <Spin />
          </div>
        ) : rows.length === 0 ? (
          <div style={{ textAlign: "center", padding: 60, color: "#999" }}>
            No data found
          </div>
        ) : (
          <Table
            columns={columns}
            dataSource={rows}
            rowKey={(r, i) => r.invoiceNo?.toString() || `row-${i}`}
            pagination={false}
            scroll={{ x: "max-content", y: 500 }}
            components={components}
            sticky
          />
        )}
      </Card>
    </div>
  );
};

export default BillEntryReport;
